import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { FormController } from '@adornis/forms/x-form-controller.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import '@chancenwerk/components/chancen-component-zip-city-picker.js';
import type { ZipCity } from '@chancenwerk/db/adresse.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { KPF } from '../../../../src/public-access/api/KPFPublicInformationAPI.js';
import ImageBergBelow from '../assets/logo_chancenreise_untereinander.svg';
import ImageWelcome from './../assets/welcome-small-min.png';
import { AnmeldungPageRegistrationBase } from './anmeldung-component-formular-base.js';

/**
 * @customElement anmeldung-component-formular-school-info
 */
@customElement('anmeldung-component-formular-school-info')
export class AnmeldungPageRegistrationSchoolInfo extends AnmeldungPageRegistrationBase {
  @property({ attribute: false }) form!: FormController<Student>;
  @property({ attribute: false }) translation: TranslationController<any> | null = null;
  @property({ attribute: 'school-level' }) schoolLevel: 'us' | 'os' = 'us';
  @property({ attribute: false }) schools: string[] = [];
  @property({ attribute: false }) showSchoolsDirectly = false;
  @property({ attribute: false }) oldSchoolCityForFilter: Maybe<ZipCity>;
  @property({ attribute: false }) schoolCityForFilter!: RXController<Maybe<ZipCity>>;
  @property({ attribute: false }) student!: RXController<Student>;
  @property({ attribute: false }) choosenKPF!: RXController<KPF | undefined>;
  @property({ attribute: false }) kpfs: KPF[] = [];
  @property({ attribute: false }) image: Maybe<string> = null;

  override render() {
    return html` <cr-flex class="section-container" space="lg">
      <cr-image
        disabled
        .value=${this.image ?? (this.schoolLevel === 'os' ? ImageBergBelow : ImageWelcome)}
        mode="contain"
        ${css({ marginTop: 0 })}
      >
      </cr-image>

      <cr-flex class="sc-content">
        <cr-flex space="lg" ${css({ minHeight: '300px' })}>
          <cr-flex space="md">
            <cr-h4 ${css({ padding: `0 ${this.spacing.sm}` })}>
              ${this.translation?.translate('step1', this.schoolLevel, 'schoolQuestion')}
            </cr-h4>
            ${
              !this.showSchoolsDirectly
                ? html`<chancen-component-zip-city-picker
                    only-cities
                    placeholder-city=${'Stadt *'}
                    .value=${this.schoolCityForFilter.value}
                    @value-picked=${(e: ValueEvent<Maybe<ZipCity>>) => {
                      this.schoolCityForFilter.next(e.detail.value);

                      if (
                        !this.oldSchoolCityForFilter?.city ||
                        !this.schoolCityForFilter.value?.city ||
                        this.oldSchoolCityForFilter.city !== this.schoolCityForFilter.value.city
                      ) {
                        this.choosenKPF.next(undefined);
                        this.oldSchoolCityForFilter = e.detail.value;
                      }
                    }}
                  ></chancen-component-zip-city-picker>`
                : nothing
            }
            </chancen-component-zip-city-picker>
            ${
              !this.schoolCityForFilter.value?.city?.length && !this.showSchoolsDirectly
                ? html`
                    <cr-text ${css({ padding: `0 ${this.spacing.sm}`, color: this.colors.accent.shade(300) })}>
                      ${this.translation?.translate('step1', this.schoolLevel, 'cityReminder')}
                    </cr-text>
                  `
                : this.schools.length
                ? html`
                    <cr-flex horizontal space="md" crossaxis-center>
                      <cr-dropdown-selection
                        ${css({ flex: '1', width: '0' })}
                        placeholder=${this.translation?.translate('step1', 'school') + ' *'}
                        .selectables=${this.schools}
                        .renderString=${(kpfID: Maybe<string>) =>
                          kpfID ? this.kpfs.find(p => p._id === kpfID)?.school?.name ?? '' : ''}
                        @value-picked=${(e: ValueEvent<string>) => {
                          const kpf = this.kpfs.find(p => p._id === e.detail.value);
                          this.choosenKPF.next(kpf);
                        }}
                      >
                      </cr-dropdown-selection>
                      <cr-icon ${css({ cursor: 'pointer', flex: '2' })}>
                        info
                        <cr-tooltip clickable>
                          <cr-text ${css({ maxWidth: '400px' })}
                            >${this.translation?.translate('tooltips', 'school')}
                          </cr-text>
                        </cr-tooltip>
                      </cr-icon>
                    </cr-flex>

                    ${!this.choosenKPF.value
                      ? html`
                          <cr-text ${css({ padding: `0 ${this.spacing.sm}`, color: this.colors.accent.shade(300) })}>
                            ${this.translation?.translate('step1', this.schoolLevel, 'schoolReminder')}
                          </cr-text>
                        `
                      : html` <cr-flex space="md" center>
                          <cr-flex horizontal wrap crossaxis-center center space="md">
                            <cr-dropdown-selection
                              ${css({ flex: '1', flexWrap: 'wrap' })}
                              ${this.form.field('schoolInformation', 'jahrgangsstufe')}
                              placeholder=${this.translation?.translate('step1', 'grade') + ' *'}
                              .selectables=${this.schoolLevel === 'os'
                                ? [7, 8, 9, 10, 11, 12, 13]
                                : [1, 2, 3, 4, 5, 6, 7, 8]}
                            ></cr-dropdown-selection>

                            <cr-flex horizontal center crossaxis-center space="md" class="mobile--fullwidth">
                              <cr-input
                                ${css({ flex: '1' })}
                                ${this.form.field('schoolInformation', 'zug')}
                                placeholder=${this.translation?.translate('step1', 'zug')}
                              ></cr-input>
                              <cr-icon ${css({ cursor: 'pointer', flex: '2' })}>
                                info
                                <cr-tooltip clickable>
                                  <cr-text ${css({ maxWidth: '400px' })}
                                    >${this.translation?.translate('tooltips', 'zug')}</cr-text
                                  >
                                </cr-tooltip>
                              </cr-icon>
                            </cr-flex>
                          </cr-flex>
                          <cr-input
                            ${this.form.field('schoolInformation', 'klassenlehrer')}
                            flex
                            placeholder=${this.translation?.translate('step1', 'teacher')}
                          ></cr-input>
                        </cr-flex>`}
                  `
                : html`<cr-text>
                    ${this.showSchoolsDirectly ? '' : this.translation?.translate('step1', 'noSchools')}
                  </cr-text>`
            }

            <slot></slot>
          </cr-flex>

          <cr-flex horizontal end crossaxis-center space="sm">
            <cr-button
              @click=${async () => {
                this.form.validate().catch(console.error);
              }}
            >
              ${this.translation?.translate('next')}
            </cr-button>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    </cr-flex>`;
  }
}
